import type { ConciergeCommerceAdapter, ConciergeCommerceProduct, ConciergeCartState } from '../types/commerce';

/**
 * Shopify storefront commerce adapter — the shared implementation.
 *
 * ## Why this lives in common-ui and not in one host
 *
 * The concierge reaches a merchant's Shopify cart from TWO surfaces, and both
 * are same-origin with the storefront:
 *
 *   1. the full page at `thestore.com/a/sos/concierge` (App Proxy), hosted by
 *      `landing-page`, and
 *   2. the storefront BUBBLE, hosted by `brandclub-checkout-shop-app` through
 *      `ConciergeStandalone` and mounted by our `ask-us` theme app embed.
 *
 * It started life in `landing-page/src/utils/shopifyCommerce.ts`, so only (1)
 * had it. Since every add-to-cart control in the concierge is gated on the
 * PRESENCE of an adapter (D-41 for the product panel's "Add to bag",
 * `MfWidgetLoader` for the product cards), the bubble silently had no way to
 * buy anything — on the merchant's own storefront, where the AJAX Cart API is
 * one same-origin fetch away. Same code, two hosts, one place.
 *
 * ## The API used
 *
 *   GET  /products/<handle>.js  → product + variants (resolve step)
 *   POST /cart/add.js           → add line items
 *   POST /cart/update.js        → cart attributes (the concierge visitor stamp)
 *   GET  /cart.js               → cart state (item_count)
 *   /cart, /checkout            → Shopify's native pages (storefront ROOT
 *                                 paths — NOT under the /a/sos sub-path)
 *
 * Concierge products carry no Shopify variant id — only a product-page URL
 * (…/products/<handle>[?variant=<id>]) plus the retailerId>100000 DTC/Shopify
 * convention. The URL lives in `dtcProductPageUrl` OR (Catalog-v2 payloads)
 * `retailerProductPageUrl` — check both. So `addToCart` resolves handle →
 * variant at add time; the /products/<handle>.js fetch doubles as the
 * authoritative "is this product on THIS store" check (404 → {ok:false} → the
 * card falls back to its link-out).
 */

interface ShopifyVariant {
  id: number;
  available: boolean;
}

interface ShopifyProductJs {
  variants?: ShopifyVariant[];
}

/** DTC/Shopify retailers live in the 100000+ id range (repo-wide convention). */
const isShopifyRetailerId = (retailerId?: number): boolean => typeof retailerId === 'number' && retailerId > 100000;

/**
 * Extract the Shopify product handle (and optional `?variant=` hint) from a
 * product-page URL like `https://store.mybrand.rocks/products/blue-tee?variant=123`.
 * Returns null when the URL doesn't look like a Shopify product page.
 */
export function parseShopifyProductUrl(url: string | undefined): { handle: string; variantId: number | null } | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    const variantParam = parsed.searchParams.get('variant');
    const variantId = variantParam && /^\d+$/.test(variantParam) ? Number(variantParam) : null;
    return { handle: match[1], variantId };
  } catch {
    return null;
  }
}

/** The Shopify product-page URL for a concierge product, wherever the payload put it. */
const getProductPageUrl = (product: ConciergeCommerceProduct): string | undefined =>
  product.dtcProductPageUrl ?? product.retailerProductPageUrl;

export interface ShopifyCommerceAdapterOptions {
  /**
   * A host-owned re-check run on every `canAddToCart`.
   *
   * `landing-page` passes `isBackRoom` because its route can change under the
   * same adapter instance (front-room and back-room are the same SPA build).
   * The bubble host has no such transition — the page it is mounted on IS the
   * storefront — so it passes nothing and the default is always true.
   */
  isSupportedSurface?: () => boolean;
}

/**
 * Cart attribute carrying the concierge visitor id.
 *
 * A SINGLE leading underscore is deliberate. Shopify treats it as private — hidden from
 * the shopper's cart and checkout display — while leaving it readable through
 * `/cart.js`, which is what lets the stamp be verified in the browser before any order
 * exists. A double underscore is also accepted by `/cart/update.js` but is NOT readable
 * back, and the POST returns 200 either way, so a silent rejection would be
 * indistinguishable from success until an order was placed. Measured on the live
 * storefront, 2026-09-15.
 */
const CONCIERGE_VISITOR_CART_ATTRIBUTE = '_bcConciergeVisitorId';

/**
 * Cart attribute carrying the opaque per-conversation touch.
 *
 * Distinct from the visitor marker on purpose. `_bcConciergeVisitorId` identifies a
 * BROWSER, so it can match many conversations and the resolver has to infer a winner.
 * This token is 1:1 with ONE qualifying conversation, which makes the join direct and
 * auditable — and it exposes no durable identity to Shopify, since it is random rather
 * than derived from the shopper.
 */
const CONCIERGE_TOUCH_CART_ATTRIBUTE = '_bcConciergeTouchId';

/**
 * Did the cart actually retain the stamp?
 *
 * `/cart/update.js` echoes the updated cart, so the usual case costs no extra request.
 * Some storefronts answer with a shape that omits `attributes`; rather than assume, fall
 * back to one `/cart.js` read. Any failure to confirm returns false, which leaves the
 * value un-cached and therefore retryable — the safe direction.
 */
async function attributesEqual(updateResponse: Response, expected: Record<string, string>): Promise<boolean> {
  const readAttributes = async (res: Response): Promise<Record<string, unknown> | undefined> => {
    try {
      const body = (await res.json()) as { attributes?: Record<string, unknown> } | null;
      return body?.attributes;
    } catch {
      return undefined;
    }
  };

  const matchesAll = (actual?: Record<string, unknown>) =>
    !!actual && Object.entries(expected).every(([key, value]) => actual[key] === value);

  const fromUpdate = await readAttributes(updateResponse);
  if (fromUpdate) return matchesAll(fromUpdate);

  const res = await fetch('/cart.js', { headers: { accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) return false;
  return matchesAll(await readAttributes(res));
}

export function createShopifyCommerceAdapter(options: ShopifyCommerceAdapterOptions = {}): ConciergeCommerceAdapter {
  const isSupportedSurface = options.isSupportedSurface ?? (() => true);
  /** The visitor id this cart is already stamped with, so repeated tracking callbacks
   *  collapse to one POST. Only set after a CONFIRMED success — a failed stamp must
   *  stay retryable rather than being cached as done. */
  let stampedVisitorId: string | null = null;
  /** In-flight stamp for the same visitor, so concurrent callbacks share one request
   *  instead of racing three POSTs at the same cart. */
  let stampInFlight: { visitorId: string; promise: Promise<void> } | null = null;
  /** Cache key for the dual-write path — `visitorId|touchId`, so a changed touch
   *  re-stamps even when the browser identity is unchanged. */
  let stampedAttributionKey: string | null = null;
  let attributionInFlight: { key: string; promise: Promise<void> } | null = null;
  let state: ConciergeCartState = { itemCount: 0 };
  const listeners = new Set<(s: ConciergeCartState) => void>();

  const setState = (next: ConciergeCartState) => {
    state = next;
    listeners.forEach((l) => {
      try {
        l(state);
      } catch {
        // A broken subscriber must not take down the others.
      }
    });
  };

  const refreshCart = async (): Promise<void> => {
    try {
      const res = await fetch('/cart.js', {
        headers: { accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const cart = (await res.json()) as { item_count?: number };
      if (typeof cart.item_count === 'number') {
        setState({ itemCount: cart.item_count });
      }
    } catch (err) {
      console.error('[shopifyCommerce] cart.js fetch failed:', err);
    }
  };

  const stampConciergeVisitor = async (visitorId: string): Promise<void> => {
    const id = visitorId?.trim();
    // Posting an empty value would CLEAR an attribute a previous stamp set, quietly
    // un-attributing a conversation that was already linked.
    if (!id) return;
    if (stampedVisitorId === id) return;
    if (stampInFlight?.visitorId === id) return stampInFlight.promise;

    const promise = (async () => {
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: { [CONCIERGE_VISITOR_CART_ATTRIBUTE]: id } }),
        });
        if (!res.ok) return;

        // A 2xx is NOT proof of persistence. A theme or app can strip or rewrite cart
        // attributes, and Shopify still answers 200 — so caching success here would
        // leave us believing the cart is stamped while the order carries nothing, and
        // the suppressed retry removes the only chance to notice. Confirm the exact
        // value came back before recording it.
        if (await attributesEqual(res, { [CONCIERGE_VISITOR_CART_ATTRIBUTE]: id })) stampedVisitorId = id;
      } catch {
        // Attribution is best-effort. Swallow, leave `stampedVisitorId` unset so the
        // next qualifying event retries, and never surface this to the shopper.
      } finally {
        stampInFlight = null;
      }
    })();

    stampInFlight = { visitorId: id, promise };
    return promise;
  };

  /**
   * Write the concierge attribution markers onto the cart, ATOMICALLY.
   *
   * One request, both attributes. Two separate writes could leave the cart carrying a
   * touch without its legacy marker (or the reverse) if the second failed — and the
   * legacy marker has to keep working unchanged until order-level parity proves nothing
   * still depends on it.
   *
   * Deliberately a separate method rather than an overload of `stampConciergeVisitor`:
   * that one has a single-value contract and existing callers, and quietly widening it
   * to carry a touch would make its cache key wrong.
   */
  const stampConciergeAttribution = async (args: { visitorId?: string; touchId?: string }): Promise<void> => {
    const visitorId = args.visitorId?.trim();
    const touchId = args.touchId?.trim();

    const attributes: Record<string, string> = {};
    // Empty values are omitted, never sent: posting an empty string CLEARS an attribute a
    // previous stamp set, silently un-attributing a conversation already linked.
    if (visitorId) attributes[CONCIERGE_VISITOR_CART_ATTRIBUTE] = visitorId;
    if (touchId) attributes[CONCIERGE_TOUCH_CART_ATTRIBUTE] = touchId;
    if (Object.keys(attributes).length === 0) return;

    const cacheKey = `${visitorId ?? ''}|${touchId ?? ''}`;
    if (stampedAttributionKey === cacheKey) return;
    if (attributionInFlight?.key === cacheKey) return attributionInFlight.promise;

    const promise = (async () => {
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes }),
        });
        if (!res.ok) return;
        // Every attribute must read back. A store that keeps the legacy marker but drops
        // the touch would otherwise cache as success, leaving the direct link absent
        // while we believe it landed.
        if (await attributesEqual(res, attributes)) stampedAttributionKey = cacheKey;
      } catch {
        // Best-effort. Leave the key uncached so the next qualifying event retries.
      } finally {
        attributionInFlight = null;
      }
    })();

    attributionInFlight = { key: cacheKey, promise };
    return promise;
  };

  const adapter: ConciergeCommerceAdapter = {
    provider: 'shopify',
    stampConciergeVisitor,
    stampConciergeAttribution,

    canAddToCart(product: ConciergeCommerceProduct): boolean {
      return (
        isSupportedSurface() &&
        isShopifyRetailerId(product.retailerId) &&
        parseShopifyProductUrl(getProductPageUrl(product)) !== null
      );
    },

    async addToCart(product: ConciergeCommerceProduct) {
      const ref = parseShopifyProductUrl(getProductPageUrl(product));
      if (!ref) return { ok: false as const, error: 'no_product_handle' };

      // Resolve handle → variant. A 404 means the product isn't on this store.
      let productJs: ShopifyProductJs;
      try {
        const res = await fetch(`/products/${ref.handle}.js`, {
          headers: { accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!res.ok) return { ok: false as const, error: `product_lookup_${res.status}` };
        productJs = (await res.json()) as ShopifyProductJs;
      } catch (err) {
        console.error('[shopifyCommerce] product lookup failed:', err);
        return { ok: false as const, error: 'product_lookup_failed' };
      }

      const variants = productJs.variants ?? [];
      const hinted = ref.variantId ? variants.find((v) => v.id === ref.variantId) : undefined;
      const variant = hinted ?? variants.find((v) => v.available) ?? variants[0];
      if (!variant) return { ok: false as const, error: 'no_variant' };

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [{ id: variant.id, quantity: product.quantity ?? 1 }] }),
        });
        if (!res.ok) {
          // Shopify returns 422 with {description} for e.g. sold-out variants.
          let description = `cart_add_${res.status}`;
          try {
            const body = (await res.json()) as { description?: string };
            if (body?.description) description = body.description;
          } catch {
            // Non-JSON error body — keep the status-based description.
          }
          return { ok: false as const, error: description };
        }
      } catch (err) {
        console.error('[shopifyCommerce] cart/add.js failed:', err);
        return { ok: false as const, error: 'cart_add_failed' };
      }

      await refreshCart();
      return { ok: true as const, itemCount: state.itemCount };
    },

    getCartState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    refreshCart,

    // Shopify's native pages live at the storefront ROOT — deliberately not
    // withRoutePath()'d.
    goToCart: () => window.location.assign('/cart'),
    goToCheckout: () => window.location.assign('/checkout'),
  };

  return adapter;
}

/**
 * The Shopify storefront cart for the BUBBLE host.
 *
 * This is NOT the whole bag choice. A brand with checkout on fills the
 * Brandclub bag instead, and a store whose hosting surface is not the Shopify
 * App Proxy gets no bag. The host makes that choice (checkout-shop-app
 * `conciergeCommerce.ts`) and calls this only for the Shopify-cart case.
 *
 * `window.bcStoreKind` is published by our `ask-us` theme app embed, and by
 * nothing else in the codebase — so it means exactly "this bubble is mounted on
 * a Shopify storefront by our own extension". That is a host DECLARATION, not a
 * guess from the page's shape, which is the distinction D-31 exists to keep:
 * we never sniff the DOM to decide what surface we are on.
 *
 * A Brandclub-domain mount (shop.<brand>.com) never sets it, so it gets no
 * adapter and keeps today's link-out behaviour.
 *
 * Singleton, so cart state survives the widget being closed and reopened.
 */
let cachedStorefrontAdapter: ConciergeCommerceAdapter | null | undefined;

export function getShopifyStorefrontCommerceAdapter(): ConciergeCommerceAdapter | undefined {
  if (typeof window === 'undefined') return undefined;
  if (cachedStorefrontAdapter === undefined) {
    const declaredStoreKind = (window as { bcStoreKind?: string }).bcStoreKind;
    cachedStorefrontAdapter = declaredStoreKind === 'shopify' ? createShopifyCommerceAdapter() : null;
    // Seed the badge/chip with the shopper's existing Shopify cart.
    cachedStorefrontAdapter?.refreshCart();
  }
  return cachedStorefrontAdapter ?? undefined;
}
